import React, { useState, useLayoutEffect, useRef } from 'react';

import { flushSync } from 'react-dom';
import { useSelector } from 'react-redux';
import { Button } from 'react-bootstrap';

import _ from 'lodash';
import cx from 'classnames';
import Portal from 'components/Portal';
import { IKImage } from 'imagekitio-react';
import Draggable from 'react-draggable';
import Moveable from "react-moveable";

import { Property } from '../../components/properties/';
import styles from './image.module.scss';

const maskTypes =  [
  'square', 'circle', 'triangle', 'hexagon', 'octagon', 'star', 'egg', 'diamond', 'semi_circle', 'heart', 'moon', 'plus', 'trapezium', 'extended_arrow', 'arrow'
]

const maskImgBaseURL = process.env.REACT_APP_CLOUD_FRONT_URL + '/public/cleverstory/v2/mask/';

export const ImageCropper = ({ editorSession, widgets, images, show, crop, width, height, onCrop, onCancel }) => {
  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));
  const cropperRef = useRef(null);
  const imageRef = React.useRef(null);
  const [settingsMap, setSettingsMap] = useState({});
  const isMultiSelect = widgets.length > 1;

  const calculateInitialSettings = (widget, image, cropData) => {
    const id = widget.uid;
    const layout = _.get(widget, `properties.${breakpoint}.layout`, {});
    const width = parseInt(layout.width);
    const height = parseInt(layout.height);

    const innerWidth = _.get(cropData, 'width', width);
    const innerHeight = _.get(cropData, 'height', height);

    const scaleX = width / innerWidth;
    const scaleY = height / innerHeight;

    const zoomWidth = scaleX * _.get(cropData, 'zoomWidth', width);
    const zoomHeight = scaleY * _.get(cropData, 'zoomHeight', height);

    const mask = _.get(cropData, 'mask', `${maskImgBaseURL}${maskTypes[0]}.svg`);
    const rect = document.querySelector(`#${id}`)?.getBoundingClientRect() || { left: 0, top: 0 };

    return {
      image,
      mask,
      element: { x: rect.left, y: rect.top, width, height },
      inner: {
        x: _.get(cropData, 'x', 0) * scaleX,
        y: _.get(cropData, 'y', 0) * scaleY,
        width,
        height
      },
      outer: {
        x: rect.left - (_.get(cropData, 'x', 0) * scaleX),
        y: rect.top - (_.get(cropData, 'y', 0) * scaleY),
        width: zoomWidth,
        height: zoomHeight
      }
    };
  };

  useLayoutEffect(() => {
    if (!show) return;
    const newSettings = {};
    const isBulk = widgets.length > 1;

    widgets.forEach((widget, i) => {
      newSettings[widget.uid] = calculateInitialSettings(widget, images[i], crop || {});
      if(isBulk) {
        newSettings[widget.uid].mask = '';
      }
    });

    setSettingsMap(newSettings);
    document.querySelector(`#${editorSession}`)?.classList.add(styles.disableScroll);
    return () => {
      document.querySelector(`#${editorSession}`)?.classList.remove(styles.disableScroll);
    };
  }, [show, widgets, images]);

  const updateSettings = (uid, updatedSettings) => {
    flushSync(() => {
    setSettingsMap(prev => ({
      ...prev,
      [uid]: {
        ...prev[uid],
        ...updatedSettings
      }
    }));
    })
  };

  const updateMaskForAll = (mask) => {
    setSettingsMap(prev => {
      const updated = {};
      for (const uid in prev) {
        updated[uid] = { ...prev[uid], mask: `${maskImgBaseURL}${mask}.svg` };
      }
      return updated;
    });
  };

  const handleCropConfirm = () => {
    let updatedCrops = {};
    let updatedLayouts = {};

    widgets.forEach(widget => {
      const uid = widget.uid;
      const settings = settingsMap[uid];
      const rect = document.querySelector(`#${uid}`)?.getBoundingClientRect() || { left: 0, top: 0 };

      // Here we are setting as object to update the property changes in saga
      updatedCrops[uid] = {
        crop: {
          x: settings.inner.x,
          y: settings.inner.y,
          width: settings.inner.width,
          height: settings.inner.height,
          zoomWidth: settings.outer.width,
          zoomHeight: settings.outer.height,
          mask: settings.mask
        }
      };

      // Here we are setting as array to update the layout changes in saga
      updatedLayouts[uid] = {
        id: uid,
        layout: {
          ...settings.element,
          deltaX: settings.element.x - rect.left,
          deltaY: settings.element.y - rect.top
        }
      };
    });

    const uid = widgets?.[0].uid;

    updatedCrops = Object.keys(updatedCrops).length > 1 ? updatedCrops : updatedCrops[uid].crop || {};
    updatedLayouts = Object.keys(updatedLayouts).length > 1 ? updatedLayouts : updatedLayouts[uid].layout || {};

    onCrop(updatedCrops, updatedLayouts);
  };

  const handleDrag = (e, data, uid) => {
    setSettingsMap((prev) => {
      const state = prev[uid];

      const deltaX = data.x - state.outer.x;
      const deltaY = data.y - state.outer.y;

      const originX = state.inner.originX - (deltaX * 50 / state.outer.width);
      const originY = state.inner.originY - (deltaY * 50 / state.outer.height);

      const updated = {
        ...state,
        outer: {
          ...state.outer,
          x: data.x,
          y: data.y
        },
        inner: {
          ...state.inner,
          originX: originX,
          originY: originY,
          x: state.inner.x - deltaX,
          y: state.inner.y - deltaY
        }
      }

      return {
        ...prev,
        [uid]: updated
      }
    })
  }

  const handleResize = (data, uid) => {
    flushSync(() => {
      setSettingsMap((prev) => {
        const state = prev[uid];
        
        var { x, y, width, height } = data;

        x = Math.max(x, 0);
        y = Math.max(y, 0);

        width = Math.min(width, state.outer.width);
        height = Math.min(height, state.outer.height);

        const updated = {
          ...state,
          inner: {
            ...state.inner,
            ...data,
            x: x,
            y: y,
            width: width,
            height: height,
          },

          element: {
            ...state.element,
            x: state.outer.x + x,
            y: state.outer.y + y,
            width: width,
            height: height,
          },
        };

        return {
          ...prev,
          [uid]: updated
        }
      });
    });
  };

  const imageElementRender = (uid, i) => {
    const settings = settingsMap[uid] || {
      dialogPosition: null,
      image: images?.[i] || {},
      mask: '',
      element: {
        x: _.get(crop, 'x', 0),
        y: _.get(crop, 'y', 0),
        width: parseInt(width),
        height: parseInt(height)
      },
      inner: {
        x: _.get(crop, 'x', 0),
        y: _.get(crop, 'y', 0),
        originX: 50,
        originY: 50,
        width: parseInt(width),
        height: parseInt(height)
      },
      outer: {
        x: 0,
        y: 0,
        width: _.get(crop, 'zoomWidth', parseInt(width)),
        height: _.get(crop, 'zoomHeight', parseInt(height))
      }
    };
    const innerStyle = {
      left: settings.inner.x,
      top: settings.inner.y,
      width: settings.inner.width,
      height: settings.inner.height,
      maskImage: `url(${settings.mask})`,
      WebkitMaskImage: `url(${settings.mask})`,
      maskSize: '100% 100%',
      WebkitMaskSize: '100% 100%'
    };
    return (
      <Draggable
        key={uid}
        onDrag={(e, data) => handleDrag(e, data, uid)}
        position={{ x: settings.outer.x, y: settings.outer.y }}
        bounds={{
          left: settings.element.x + settings.element.width - settings.outer.width,
          right: settings.element.x,
          top: settings.element.y + settings.element.height - settings.outer.height,
          bottom: settings.element.y
        }}
        cancel={'.moveable-control'}
      >
        <div className={cx(styles.outer, styles.handle)}>
          <IKImage
            ref={imageRef}
            path={images[i]?.url}
            width={settings.outer.width}
            height={settings.outer.height}
            draggable="false"
            transformation={[{ format: 'auto', effectUSM: '0.66-1.00-0.01' }]}
          />
          <div className={cx(styles.inner, styles.handle)} style={innerStyle}>
            <IKImage
              ref={imageRef}
              path={images[i]?.url}
              width={settings.outer.width}
              height={settings.outer.height}
              draggable="false"
              transformation={[{ format: 'auto', effectUSM: '0.66-1.00-0.01' }]}
              style={{
                top: -settings.inner.y,
                left: -settings.inner.x,
                position: 'absolute'
              }}
            />
          </div>
          {!isMultiSelect && (
            <Resizable
              target={`.${styles.inner}`}
              dimension={settings.inner}
              onResize={(data) => handleResize(data, uid)}
            />
          )}
        </div>
      </Draggable>
    );
  };

  const sharedSettings = settingsMap[widgets[0]?.uid] || {};

  return (
    <Portal>
      <div ref={cropperRef} className={cx(styles.cropper, { [styles.show]: show })}>
        {(widgets.length > 0 
          ? widgets.map((widget, i) => imageElementRender(widget.uid, i)) 
          : imageElementRender('', 0)
        )}

        {show &&
          <CropperSettings
            ids={widgets.map(w => w.uid)}
            image={images[0]}
            value={sharedSettings}
            isSingleSelection={!isMultiSelect}
            onChange={(val) => {
              if (!isMultiSelect) {
                const uid = widgets[0].uid;
                const settings = settingsMap[uid];
                const height = val * settings.outer.height / settings.outer.width;
                updateSettings(uid, {
                  outer: {
                    ...settings.outer,
                    width: val,
                    height
                  }
                });
              }
            }}
            onCrop={handleCropConfirm}
            onCancel={onCancel}
            handleMaskChange={updateMaskForAll}
          />
        }
      </div>
    </Portal>
  );
};

const CropperSettings = ({
  ids = [],
  value,
  image,
  isSingleSelection,
  onChange,
  onCrop,
  onCancel,
  handleMaskChange
}) => {
  if (!value || !_.size(value)) return null;

  let minZoom = 0;
  if (value.image) {
    const aspectRatio = value.image.width / value.image.height;
    minZoom = Math.max(value.inner.width, value.inner.height * aspectRatio);
  }

  return (
    <Property.Dialog className={styles.cropperDialog} width={260} target={`#${ids[0]}`}>
      <Property.Header title="Crop Image">
        <Button className={cx(styles.action, styles.confirm)} onClick={onCrop}>
          <i className="cleverstory-tick" />
        </Button>
        <Button className={styles.action} onClick={onCancel}>
          <i className="cleverstory-close" />
        </Button>
      </Property.Header>

      <Property.Body>
        {isSingleSelection && (
          <Property className={styles.zoomImage}>
            <h5 className={styles.title}>Zoom image</h5>
            <Property.Slider
              min={minZoom}
              max={minZoom * 4}
              step={1}
              value={value.outer.width}
              onChange={onChange}
            />
          </Property>
        )}

        <Property className={styles.dialogBody}>
          <div className={styles.maskContainer}>
            <h5 className={styles.title}>Crop with shape</h5>
            <div className={styles.grid}>
              {maskTypes.map((mask, i) => {
                const isActive = value.mask === `${maskImgBaseURL}${mask}.svg`;

                const style = {
                  maskImage: `url(${maskImgBaseURL}${mask}.svg)`,
                  WebkitMaskImage: `url(${maskImgBaseURL}${mask}.svg)`,
                  maskRepeat: 'no-repeat',
                  WebkitMaskRepeat: 'no-repeat',
                  maskPosition: 'center',
                  WebkitMaskPosition: 'center'
                };

                return (
                  <div
                    key={i}
                    className={cx(styles.maskTypeButton, { [styles.active]: isActive })}
                    onClick={() => handleMaskChange(mask)}
                  >
                    <div className={styles.icon}>
                      <div style={style}>
                        <IKImage
                          path={image?.url || ''}
                          width={60}
                          height={60}
                          className={styles.mask}
                          draggable="false"
                          transformation={[{ format: 'auto', effectUSM: '0.66-1.00-0.01' }]}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </Property>
      </Property.Body>
    </Property.Dialog>
  );
};


const Resizable = ({ target, dimension, onResize, onResizeEnd }) => {
  const moveableRef = React.useRef();
  const [targetElement, setTargetElement] = React.useState();
  const [frame, setFrame] = React.useState({
    translate: [dimension.x, dimension.y]
  });

  React.useLayoutEffect(() => {
    setTargetElement(document.querySelector(target));
  }, []);

  useLayoutEffect(() => {
    setFrame({
      translate: [dimension.x, dimension.y]
    })
    moveableRef.current.updateRect();
  }, [dimension]);

  return (
    <Moveable
      flushSync={flushSync}
      ref={moveableRef}
      target={targetElement}
      resizable={true}
      keepRatio={false}
      renderDirections={['nw', 'n', 'ne', 'w', 'e', 'sw', 's', 'se']}
      edge={false}
      origin={false}
      zoom={1}
      onResizeStart={e => {
        e.setOrigin(["%", "%"]);
        e.dragStart && e.dragStart.set(frame.translate);
      }}
      onResize={e => {
        const beforeTranslate = e.drag.beforeTranslate;
        frame.translate = beforeTranslate;

        const change = {
          ...dimension,
          width: e.width,
          height: e.height,
          x: beforeTranslate[0],
          y: beforeTranslate[1]
        }

        onResize(change);
      }}
    />
  );
}